import type {
  NhlTiebreakerTeam, TiebreakerResult, GameResultsLookup, TiebreakerOptions,
  StepContext,
} from '../../shared/tiebreaker/types';
import { RankMethod } from '../../shared/enums/rank-method';
import { createContext } from '../../shared/tiebreaker/step-runner';
import { groupByDescending, addExplanation } from '../../shared/tiebreaker/helpers';
import { NHL_CONF_CRITERIA, NHL_CONF_STEPS, type NhlCriterion } from './nhl-steps';

function runNhlTiebreakerSteps(
  teams: NhlTiebreakerTeam[],
  criteria: NhlCriterion[],
  ctx: StepContext<NhlTiebreakerTeam>,
): TiebreakerResult<NhlTiebreakerTeam> {
  const orderedTeams = rankNhlSubgroup(teams, criteria, ctx, 0);
  return {
    orderedTeams,
    rankMethods: ctx.rankMethods,
    explanations: ctx.explanations,
    explainerDetails: ctx.explainerDetails,
  };
}

function rankNhlSubgroup(
  teams: NhlTiebreakerTeam[],
  criteria: NhlCriterion[],
  ctx: StepContext<NhlTiebreakerTeam>,
  startStep: number,
): NhlTiebreakerTeam[] {
  if (teams.length <= 1) {
    return [...teams];
  }

  for (let stepIdx = startStep; stepIdx < criteria.length; stepIdx++) {
    const criterion = criteria[stepIdx];
    const result = criterion.step(teams, ctx);
    if (result.winners.length === 0 && result.remaining.length === teams.length) {
      continue;
    }

    const score = criterion.score(teams, ctx);
    const groups = groupByDescending(teams, score);
    if (criterion.label != null) {
      for (const team of result.remaining) {
        addExplanation(ctx, team, `${criterion.label}: ${score(team).toFixed(3)}.`);
      }
    }

    const ordered: NhlTiebreakerTeam[] = [];
    for (const group of groups) {
      if (group.length === 1) {
        if (result.rankMethod != null) {
          ctx.rankMethods.set(group[0].teamAbbreviation, result.rankMethod);
        }
        ordered.push(group[0]);
      } else {
        ordered.push(...rankNhlSubgroup(group, criteria, ctx, stepIdx + 1));
      }
    }
    return ordered;
  }

  const remaining = [...teams];
  const ordered: NhlTiebreakerTeam[] = [];
  while (remaining.length > 0) {
    const winner = ctx.coinFlipFn(remaining);
    ctx.rankMethods.set(winner.teamAbbreviation, RankMethod.CoinFlip);
    addExplanation(ctx, winner, 'All NHL tiebreaker steps exhausted. Coin flip.');
    ordered.push(winner);
    remaining.splice(remaining.indexOf(winner), 1);
  }
  return ordered;
}

export function nhlConfTiebreaker(
  teams: NhlTiebreakerTeam[],
  games: GameResultsLookup,
  options?: TiebreakerOptions,
): NhlTiebreakerTeam {
  const ctx = createContext<NhlTiebreakerTeam>(games, options);
  return runNhlTiebreakerSteps(teams, NHL_CONF_CRITERIA, ctx).orderedTeams[0];
}

export function nhlConferenceRanker(
  teams: NhlTiebreakerTeam[],
  games: GameResultsLookup,
  options?: TiebreakerOptions,
): TiebreakerResult<NhlTiebreakerTeam> {
  const ctx = createContext<NhlTiebreakerTeam>(games, options);
  const ordered: NhlTiebreakerTeam[] = [];

  // Primary sort: total points (descending)
  const teamsWithGames = teams.filter(t => t.records.all.wins + t.records.all.losses > 0);
  const teamsWithoutGames = teams.filter(t => t.records.all.wins + t.records.all.losses === 0);

  const pointGroups = groupByDescending(teamsWithGames, t => t.points);
  for (const group of pointGroups) {
    if (group.length === 1) {
      ctx.rankMethods.set(group[0].teamAbbreviation, RankMethod.OverallPoints);
      ordered.push(group[0]);
    } else {
      const result = runNhlTiebreakerSteps(group, NHL_CONF_CRITERIA, ctx);
      ordered.push(...result.orderedTeams);
    }
  }

  // Zero-GP teams: coin flip
  let zeroGP = [...teamsWithoutGames];
  while (zeroGP.length > 0) {
    const winner = ctx.coinFlipFn(zeroGP);
    ctx.rankMethods.set(winner.teamAbbreviation, RankMethod.CoinFlip);
    addExplanation(ctx, winner, 'No games played. Coin flip.');
    ordered.push(winner);
    zeroGP = zeroGP.filter(t => t !== winner);
  }

  return { orderedTeams: ordered, rankMethods: ctx.rankMethods, explanations: ctx.explanations };
}

export function nhlHeadToHeadScoring(
  teams: [NhlTiebreakerTeam, NhlTiebreakerTeam],
  games: GameResultsLookup,
): NhlTiebreakerTeam | null {
  const ctx = createContext<NhlTiebreakerTeam>(games);
  const step = NHL_CONF_STEPS[4]; // h2hScoringStep
  const result = step(teams, ctx);
  if (result.winners.length === 1 && result.remaining.length === 1) {
    return result.winners[0];
  }
  return null;
}
